/**
 * @fileoverview Hook to fetch and cache all business objects (permission types) for User Management.
 *
 * The User Management page should call this hook so the fetch runs when the page loads.
 * SectorTypePlantPermissionTable and dialogs consume the same cached list for:
 * - Labeling the edited user's current permissions (e.g. C1 → "Create Containment")
 * - The list of assignable permissions (Manage User can assign any type to others).
 *
 * Cached in Redux so the list is shared and only fetched once per session.
 */

import { GetBusinessObjects } from "lib/api/modules/user";
import { MccApi } from "lib/api/mccApiWrapper";
import { UseHasPermission } from "lib/hooks/useHasPermission";
import * as log from "lib/log";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BusinessObjectsSlice } from "store/slices/businessObjectsSlice";
import { GetPermissionSortOrder } from "lib/permissions/PermissionUtils";

export type BusinessObjectForSelect = { id: string; name: string; label: string };

/**
 * @returns Cached list of all business objects (for labels and assignable permissions), loading, error, and refetch.
 */
export function UseBusinessObjects(): {
	businessObjects: { id: string; name: string }[] | null;
	availableBusinessObjects: BusinessObjectForSelect[];
	loading: boolean;
	error: string;
	refetch: () => Promise<void>;
} {
	const dispatch = useDispatch();
	const permissions = UseHasPermission();
	const list = useSelector(BusinessObjectsSlice.selectors.selectList);
	const loading = useSelector(BusinessObjectsSlice.selectors.selectLoading);
	const error = useSelector(BusinessObjectsSlice.selectors.selectError);

	const fetchBusinessObjects = useCallback(async () => {
		if (!permissions.canViewSectorAdmins && !permissions.canViewPlantUsers) {
			return;
		}
		try {
			dispatch(BusinessObjectsSlice.actions.SetLoading(true));
			dispatch(BusinessObjectsSlice.actions.SetError(""));
			const api = MccApi.GetInstance((window as any).store);
			const items = await GetBusinessObjects(api);
			dispatch(BusinessObjectsSlice.actions.SetList(items));
		} catch (e: unknown) {
			log.Errorf("Failed to fetch business objects: %j", e);
			const err = e as { response?: { data?: { message?: string } }; message?: string };
			const message =
				err?.response?.data?.message ?? err?.message ?? "Failed to load permission types.";
			dispatch(BusinessObjectsSlice.actions.SetError(message));
			dispatch(BusinessObjectsSlice.actions.SetList([]));
		} finally {
			dispatch(BusinessObjectsSlice.actions.SetLoading(false));
		}
	}, [
		permissions.canViewSectorAdmins,
		permissions.canViewPlantUsers,
		dispatch,
	]);

	useEffect(() => {
		if (
			(permissions.canViewSectorAdmins || permissions.canViewPlantUsers) &&
			list.length === 0 &&
			!loading &&
			!error
		) {
			fetchBusinessObjects();
		}
	}, [
		permissions.canViewSectorAdmins,
		permissions.canViewPlantUsers,
		list.length,
		loading,
		error,
		fetchBusinessObjects,
	]);

	const businessObjects =
		list.length > 0
			? [...list].sort((a, b) => a.name.localeCompare(b.name))
			: null;

	const availableBusinessObjects: BusinessObjectForSelect[] =
		list.length === 0
			? []
			: [...list]
					.map((bo) => ({ id: bo.id, name: bo.name, label: bo.name }))
					.sort((a, b) => GetPermissionSortOrder(a.id) - GetPermissionSortOrder(b.id));

	return {
		businessObjects,
		availableBusinessObjects,
		loading,
		error,
		refetch: fetchBusinessObjects,
	};
}
